import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import { Input } from '@roger/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Textarea } from '@roger/ui-components';
import { useToast } from '../hooks/useToastContext';
import { API_BASE_URL } from '../services/api';

import { X12_270_Response } from '../types/x12';
import X12_Response_Display from './X12_Response_Display';

interface PatientInfo {
  memberId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'M' | 'F';
}

interface InsuranceInfo {
  payorId: string;
  payorName: string;
  groupNumber?: string;
  planName?: string;
}

interface X12Request {
  patient: PatientInfo;
  insurance: InsuranceInfo;
  serviceTypes: string[];
  serviceDate?: string;
  providerNPI?: string;
}

// Define the new payer options
const payerOptions = [
  {
    value: '11111',
    label: 'X12 4010 Validation (11111)',
    description:
      'X12 Input Format - validates 270 message against 4010 version',
    category: 'X12 Input Formats',
  },
  {
    value: '22222',
    label: 'X12 5010 Validation (22222)',
    description:
      'X12 Input Format - validates 270 message against 5010 version',
    category: 'X12 Input Formats',
  },
  {
    value: '33333',
    label: 'Simulated Medicare Response (33333)',
    description: 'X12 or SF1 Input Format - fabricated Medicare response',
    category: 'Simulated Responses',
  },
  {
    value: '55555',
    label: 'Simulated Medicaid Response (55555)',
    description: 'X12 or SF1 Input Format - fabricated Medicaid response',
    category: 'Simulated Responses',
  },
  {
    value: '66666',
    label: 'Simulated Commercial Payer Response (66666)',
    description:
      'X12 or SF1 Input Format - fabricated commercial payer response',
    category: 'Simulated Responses',
  },
];

const X12_270_Testing: React.FC = () => {
  const { success, error } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [x12Response, setX12Response] = useState<X12_270_Response | null>(null);

  const [formData, setFormData] = useState<X12Request>({
    patient: {
      memberId: '102028143600',
      firstName: 'Linda',
      lastName: 'Tjernlund',
      dateOfBirth: '1956-03-09',
      gender: 'F',
    },
    insurance: {
      payorId: '',
      payorName: '',
      groupNumber: '',
      planName: '',
    },
    serviceTypes: ['30', '42', '44'], // Default to Medical Care
    serviceDate: '2025-07-15',
    providerNPI: '1417441361', //BLOSSOM RIDGE HEALTH / Palmeira
  });

  const serviceTypeOptions = [
    { value: '30', label: '(30) Health Benefit Plan Coverage' },
    { value: '42', label: '(42) Home Health Care' },
    { value: '44', label: '(44) Home Health Visits' },
  ];

  const handlePatientChange = (field: keyof PatientInfo, value: string) => {
    setFormData(prev => ({
      ...prev,
      patient: {
        ...prev.patient,
        [field]: value,
      },
    }));
  };

  const handleInsuranceChange = (field: keyof InsuranceInfo, value: string) => {
    setFormData(prev => ({
      ...prev,
      insurance: {
        ...prev.insurance,
        [field]: value,
      },
    }));
  };

  const handlePayorSelect = (payorId: string | null) => {
    if (!payorId) {
      // Clear the payor fields
      setFormData(prev => ({
        ...prev,
        insurance: {
          ...prev.insurance,
          payorId: '',
          payorName: '',
        },
      }));
      return;
    }

    const selectedPayer = payerOptions.find(p => p.value === payorId);
    if (selectedPayer) {
      setFormData(prev => ({
        ...prev,
        insurance: {
          ...prev.insurance,
          payorId: selectedPayer.value,
          payorName: selectedPayer.label,
        },
      }));
    }
  };

  const handleServiceTypesChange = (values: string[]) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: values,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setX12Response(null);

    try {
      // Use the new combined endpoint that generates X12 270 and submits to Waystar
      const eligibilityRequest = {
        information_source_id: 'ROGEREMR',
        information_source_name: 'Roger EMR',
        subscriber: {
          member_id: formData.patient.memberId,
          first_name: formData.patient.firstName,
          last_name: formData.patient.lastName,
          date_of_birth: formData.patient.dateOfBirth.replace(/-/g, ''), // Convert YYYY-MM-DD to YYYYMMDD
          gender: formData.patient.gender,
        },
        dependents: [],
        service_types: formData.serviceTypes,
        service_date: formData.serviceDate?.replace(/-/g, ''),
        provider_npi: formData.providerNPI,
        patient_info: formData.patient,
        insurance_info: formData.insurance,
      };

      // Call the new combined endpoint
      const response = await fetch(
        `${API_BASE_URL}/x12/270/eligibility-check`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(eligibilityRequest),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to process eligibility check: ${response.statusText}`
        );
      }

      const result = await response.json();

      setX12Response({
        x12Message: result.x12_270_message,
        waystarResponse: {
          http_status: result.waystar_response.http_status,
          waystar_response: result.waystar_response.waystar_response,
          x12_message: result.waystar_response.x12_message,
          x12_271_message: result.waystar_response.x12_271_message,
          x12_997_message: result.waystar_response.x12_997_message,
          parsed_271_response: result.waystar_response.parsed_271_response,
          parsed_997_response: result.waystar_response.parsed_997_response,
          message_type: result.waystar_response.message_type,
          waystar_alerts: result.waystar_response.waystar_alerts,
        },
      });

      success('X12 270 eligibility check completed successfully!');
    } catch (err) {
      console.error('Error:', err);
      setX12Response({
        x12Message: '',
        error: err instanceof Error ? err.message : 'An unknown error occurred',
      });
      error('Failed to process eligibility check');
    } finally {
      setIsLoading(false);
    }
  };

  // Group payer options by category
  const groupedPayerOptions = payerOptions.reduce(
    (acc, option) => {
      if (!acc[option.category]) {
        acc[option.category] = [];
      }
      acc[option.category].push(option);
      return acc;
    },
    {} as Record<string, typeof payerOptions>
  );

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Input Form */}
        <Card>
          <CardHeader>
            <CardTitle>Patient & Insurance Information</CardTitle>
            <CardDescription>
              Enter patient and insurance details to generate X12 270 message
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-6'>
            {/* Patient Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Patient Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='memberId'
                    className='block text-sm font-medium mb-2'
                  >
                    Member ID
                  </label>
                  <Input
                    id='memberId'
                    value={formData.patient.memberId}
                    onChange={e =>
                      handlePatientChange('memberId', e.target.value)
                    }
                    placeholder='123456789'
                  />
                </div>
                <div>
                  <label
                    htmlFor='gender'
                    className='block text-sm font-medium mb-2'
                  >
                    Gender
                  </label>
                  <Select
                    value={formData.patient.gender}
                    onValueChange={(value: 'M' | 'F') =>
                      handlePatientChange('gender', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='M'>Male</SelectItem>
                      <SelectItem value='F'>Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='firstName'
                    className='block text-sm font-medium mb-2'
                  >
                    First Name
                  </label>
                  <Input
                    id='firstName'
                    value={formData.patient.firstName}
                    onChange={e =>
                      handlePatientChange('firstName', e.target.value)
                    }
                    placeholder='John'
                  />
                </div>
                <div>
                  <label
                    htmlFor='lastName'
                    className='block text-sm font-medium mb-2'
                  >
                    Last Name
                  </label>
                  <Input
                    id='lastName'
                    value={formData.patient.lastName}
                    onChange={e =>
                      handlePatientChange('lastName', e.target.value)
                    }
                    placeholder='Doe'
                  />
                </div>
                <div className='col-span-2'>
                  <label
                    htmlFor='dateOfBirth'
                    className='block text-sm font-medium mb-2'
                  >
                    Date of Birth
                  </label>
                  <Input
                    id='dateOfBirth'
                    type='date'
                    value={formData.patient.dateOfBirth}
                    onChange={e =>
                      handlePatientChange('dateOfBirth', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Insurance Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Insurance Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div className='col-span-2'>
                  <label
                    htmlFor='payor'
                    className='block text-sm font-medium mb-2'
                  >
                    Payer Selection
                  </label>
                  <Select
                    value={formData.insurance.payorId}
                    onValueChange={handlePayorSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select a payer option' />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(groupedPayerOptions).map(
                        ([category, options]) => (
                          <div key={category}>
                            <div className='px-2 py-1.5 text-sm font-semibold text-muted-foreground bg-muted'>
                              {category}
                            </div>
                            {options.map(option => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                <div className='flex flex-col'>
                                  <span className='font-medium'>
                                    {option.label}
                                  </span>
                                  <span className='text-xs text-muted-foreground'>
                                    {option.description}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </div>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor='groupNumber'
                    className='block text-sm font-medium mb-2'
                  >
                    Group Number
                  </label>
                  <Input
                    id='groupNumber'
                    value={formData.insurance.groupNumber}
                    onChange={e =>
                      handleInsuranceChange('groupNumber', e.target.value)
                    }
                    placeholder='Optional'
                  />
                </div>
                <div>
                  <label
                    htmlFor='planName'
                    className='block text-sm font-medium mb-2'
                  >
                    Plan Name
                  </label>
                  <Input
                    id='planName'
                    value={formData.insurance.planName}
                    onChange={e =>
                      handleInsuranceChange('planName', e.target.value)
                    }
                    placeholder='Optional'
                  />
                </div>
              </div>
            </div>

            {/* Service Information */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Service Information</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='serviceDate'
                    className='block text-sm font-medium mb-2'
                  >
                    Service Date
                  </label>
                  <Input
                    id='serviceDate'
                    type='date'
                    value={formData.serviceDate}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        serviceDate: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor='providerNPI'
                    className='block text-sm font-medium mb-2'
                  >
                    Provider NPI
                  </label>
                  <Input
                    id='providerNPI'
                    value={formData.providerNPI}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        providerNPI: e.target.value,
                      }))
                    }
                    placeholder='1234567890'
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium mb-2'>
                  Service Types
                </label>
                <div className='grid grid-cols-3 gap-2 mt-2'>
                  {serviceTypeOptions.map(option => (
                    <label
                      key={option.value}
                      className='flex items-center space-x-2'
                    >
                      <input
                        type='checkbox'
                        checked={formData.serviceTypes.includes(option.value)}
                        onChange={e => {
                          if (e.target.checked) {
                            handleServiceTypesChange([
                              ...formData.serviceTypes,
                              option.value,
                            ]);
                          } else {
                            handleServiceTypesChange(
                              formData.serviceTypes.filter(
                                t => t !== option.value
                              )
                            );
                          }
                        }}
                        className='rounded'
                      />
                      <span className='text-sm'>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={isLoading}
              className='w-full'
            >
              {isLoading
                ? 'Generating and Submitting...'
                : 'Generate X12 270 & Submit to Waystar'}
            </Button>
          </CardContent>
        </Card>

        {/* Results Section */}
        {x12Response && (
          <Card>
            <CardHeader>
              <CardTitle>Results</CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* Error Display */}
              {x12Response.error && (
                <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
                  <h3 className='text-lg font-semibold text-red-800 mb-2'>
                    Error
                  </h3>
                  <p className='text-red-700'>{x12Response.error}</p>
                </div>
              )}

              {/* X12 270 Message */}
              {x12Response.x12Message && (
                <div>
                  <label className='block text-sm font-medium mb-2'>
                    Generated X12 270 Message
                  </label>
                  <Textarea
                    value={x12Response.x12Message}
                    readOnly
                    className='font-mono text-xs'
                    rows={10}
                  />
                </div>
              )}

              {/* X12 Response Display (handles 271, 997, or unknown) */}
              {x12Response.waystarResponse && (
                <X12_Response_Display
                  waystarResponse={x12Response.waystarResponse}
                />
              )}

              {/* Always show raw API response for debugging */}
              <div>
                <label className='block text-sm font-medium mb-2'>
                  Raw API Response
                </label>
                <pre className='bg-muted p-4 rounded-md text-xs overflow-auto font-mono max-h-96'>
                  {JSON.stringify(x12Response.waystarResponse, null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default X12_270_Testing;
